/**
 * 견적 계산 순수 함수 모듈
 *
 * ReactEstimateForm.tsx에서 추출한 계산 로직.
 * 모든 함수는 순수 함수(side-effect 없음)로 작성.
 */

// ──────────────────────────────────────────────
// 타입 정의
// ──────────────────────────────────────────────

export interface ShasiRow {
  id: number;
  installLocation: string;
  item: string;
  width: string;
  height: string;
  quantity: string;
  homeWindowPrice: number;
  lxHausysPrice: number;
  kccHomeCCPrice: number;
  note: string;
}

export interface OtherRow {
  id: number;
  installLocation: string;
  item: string;
  width: string;
  height: string;
  color: string;
  quantity: string;
  price: number;
  note: string;
}

export interface TongbaRow {
  id: number;
  item: string;
  spec: string;
  quantity: string;
  price: number;
}

export interface AdditionalRow {
  id: number;
  item: string;
  quantity: string;
  price: number;
  customItem: string;
  unitPrice?: number;
  priceInput?: string;
}

export interface DiscountRates {
  homeWindow: string;
  'LX하우시스': string;
  'KCC홈CC': string;
}

export interface SelectedGrades {
  LX: '프리미엄' | '스탠다드' | '베이직';
  홈윈도우: '프리미엄' | '스탠다드' | '베이직';
}

export interface BrandPrices {
  homeWindow: number;
  'LX하우시스': number;
  'KCC홈CC': number;
}

export interface Totals {
  homeWindow: { sum: number; vat: number; final: number };
  lxHausys: { sum: number; vat: number; final: number };
  kccHomeCC: { sum: number; vat: number; final: number };
}

export type UnitPricesMap = Record<string, Record<string, Record<number, number>>>;

/** fixedVariables는 DB에서 불러온 key-value. 타입은 any Record로 유지 (기존 호환) */
export type FixedVariablesMap = Record<string, number>;

// ──────────────────────────────────────────────
// 내부 타입 (calculateAllShasiPrices 파라미터)
// ──────────────────────────────────────────────

export interface ShasiCalcParams {
  unitPrices: UnitPricesMap;
  fixedVariables: FixedVariablesMap;
  discountRates: DiscountRates;
  constructionStatus: string;
  selectedGrades: SelectedGrades;
  additionalData: AdditionalRow[];
}

export interface DiscountCalcParams {
  unitPrices: UnitPricesMap;
  fixedVariables: FixedVariablesMap;
  discountRates: DiscountRates;
  selectedGrades: SelectedGrades;
}

// ──────────────────────────────────────────────
// 고정변수 추출
// ──────────────────────────────────────────────

interface ExtractedVars {
  areaUnit: number;
  minInstallationArea: number;
  baseInstallationFee: number;
  minInstallationFeeValue: number;
  baseTransportFee: number;
  wrappingPercent: number;
  homeWindowMarkupRate: number;
  lxHausysMarkupRate: number;
  kccHomeCCMarkupRate: number;
  lxStandardDiscountRate: number;
  lxBasicDiscountRate: number;
  homewindowStandardDiscountRate: number;
  homewindowBasicDiscountRate: number;
}

export function extractFixedVars(fv: FixedVariablesMap): ExtractedVars {
  return {
    areaUnit: fv.area_unit || 90000,
    minInstallationArea: fv.min_installation_area || 150,
    baseInstallationFee: fv.base_installationfee || 10000,
    minInstallationFeeValue: fv.min_installationfee || 1500000,
    baseTransportFee: fv.base_transportfee || 300000,
    wrappingPercent: fv.wrapping_percent || 0.2,
    homeWindowMarkupRate: fv.homewindow_markup_rate ?? 0,
    lxHausysMarkupRate: fv.lxhausys_markup_rate ?? 0.3,
    kccHomeCCMarkupRate: fv.kcchomecc_markup_rate ?? 0,
    lxStandardDiscountRate: fv.lxhausys_standard_discount_rate ?? 0,
    lxBasicDiscountRate: fv.lxhausys_basic_discount_rate ?? 0,
    homewindowStandardDiscountRate: fv.homewindow_standard_discount_rate ?? 0,
    homewindowBasicDiscountRate: fv.homewindow_basic_discount_rate ?? 0,
  };
}

// ──────────────────────────────────────────────
// 등급 인하율
// ──────────────────────────────────────────────

export function getGradeDiscountRates(
  grades: SelectedGrades,
  vars: ExtractedVars
): { lx: number; homewindow: number } {
  let lx = 0;
  switch (grades.LX) {
    case '스탠다드': lx = vars.lxStandardDiscountRate; break;
    case '베이직':   lx = vars.lxBasicDiscountRate; break;
    default:         lx = 0; break; // 프리미엄
  }

  let hw = 0;
  switch (grades.홈윈도우) {
    case '스탠다드': hw = vars.homewindowStandardDiscountRate; break;
    case '베이직':   hw = vars.homewindowBasicDiscountRate; break;
    default:         hw = 0; break; // 프리미엄
  }

  return { lx, homewindow: hw };
}

// ──────────────────────────────────────────────
// 면적 계산
// ──────────────────────────────────────────────

export function calculateArea(w: number, h: number, areaUnit: number): number {
  return Math.max(Math.ceil((w * h) / areaUnit));
}

// ──────────────────────────────────────────────
// 총면적 합산
// ──────────────────────────────────────────────

/** 면적 계산 대상 샷시 행 필터 */
export function filterShasiRowsForArea(rows: ShasiRow[]): ShasiRow[] {
  return rows.filter(
    (row) =>
      row.item &&
      row.item !== '-' &&
      row.item !== '망추가(블랙스텐망)' &&
      row.item !== '랩핑추가'
  );
}

export function calculateTotalArea(rows: ShasiRow[], areaUnit: number): number {
  const shasiRowsForArea = filterShasiRowsForArea(rows);
  return shasiRowsForArea.reduce((sum, row) => {
    const w = parseFloat(row.width) || 0;
    const h = parseFloat(row.height) || 0;
    if (w > 0 && h > 0) {
      const q = parseInt(row.quantity) || 1;
      return sum + calculateArea(w, h, areaUnit) * q;
    }
    return sum;
  }, 0);
}

// ──────────────────────────────────────────────
// 비공개 금액
// ──────────────────────────────────────────────

const PRIVATE_ITEMS = [
  '직접입력(고객 비공개용)',
  '비공개 금액_선택',
  '비공개 금액_주택',
  '비공개 금액_빌라',
];

export function getPrivateInputAmount(additionalData: AdditionalRow[]): number {
  return additionalData
    .filter((row) => PRIVATE_ITEMS.includes(row.item))
    .reduce((sum, row) => sum + row.price, 0);
}

export function calculateSplitAmount(privateAmount: number, rowCount: number): number {
  if (rowCount === 0) return 0;
  return Math.floor(privateAmount / rowCount / 1000) * 1000;
}

// ──────────────────────────────────────────────
// 시공비
// ──────────────────────────────────────────────

export function calculateInstallationFee(
  area: number,
  totalArea: number,
  isInstallationRequired: boolean,
  vars: ExtractedVars
): number {
  if (!isInstallationRequired) return 0;

  const basicInstallationFee = area * vars.baseInstallationFee;

  if (totalArea >= vars.minInstallationArea) {
    return basicInstallationFee;
  } else if (totalArea > 0) {
    const totalEstimated = totalArea * vars.baseInstallationFee;
    const shortage = vars.minInstallationFeeValue - totalEstimated;
    return basicInstallationFee + Math.round((shortage / totalArea) * area);
  }

  return 0;
}

// ──────────────────────────────────────────────
// 운송비
// ──────────────────────────────────────────────

export function calculateTransportFee(
  area: number,
  totalArea: number,
  baseTransportFee: number
): number {
  if (totalArea <= 0) return 0;
  return Math.round((area * baseTransportFee) / totalArea);
}

// ──────────────────────────────────────────────
// 랩핑추가
// ──────────────────────────────────────────────

export function calculateWrappingPrice(
  rows: ShasiRow[],
  unitPrices: UnitPricesMap,
  areaUnit: number,
  wrappingPercent: number
): BrandPrices {
  const totalUnitPrice = { homeWindow: 0, 'LX하우시스': 0, 'KCC홈CC': 0 };

  rows.forEach((otherRow) => {
    if (otherRow.item === '랩핑추가') return;

    const otherItem = otherRow.item?.toUpperCase();
    const otherW = parseFloat(otherRow.width) || 0;
    const otherH = parseFloat(otherRow.height) || 0;

    if (
      otherItem &&
      otherItem !== '망추가(블랙스텐망)' &&
      otherW > 0 &&
      otherH > 0
    ) {
      const area = calculateArea(otherW, otherH, areaUnit);
      const q = parseInt(otherRow.quantity) || 1;
      totalUnitPrice.homeWindow += (unitPrices['homeWindow']?.[otherItem]?.[area] || 0) * q;
      totalUnitPrice['LX하우시스'] += (unitPrices['LX하우시스']?.[otherItem]?.[area] || 0) * q;
      totalUnitPrice['KCC홈CC'] += (unitPrices['KCC홈씨씨']?.[otherItem]?.[area] || 0) * q;
    }
  });

  return {
    homeWindow: Math.floor(Math.round(totalUnitPrice.homeWindow * wrappingPercent) / 1000) * 1000,
    'LX하우시스': Math.floor(Math.round(totalUnitPrice['LX하우시스'] * wrappingPercent) / 1000) * 1000,
    'KCC홈CC': Math.floor(Math.round(totalUnitPrice['KCC홈CC'] * wrappingPercent) / 1000) * 1000,
  };
}

// ──────────────────────────────────────────────
// 전체 샷시 가격 계산 (메인 함수)
// ──────────────────────────────────────────────

export function calculateAllShasiPrices(
  shasiData: ShasiRow[],
  params: ShasiCalcParams
): ShasiRow[] {
  const { unitPrices, fixedVariables, discountRates, constructionStatus, selectedGrades, additionalData } = params;

  const vars = extractFixedVars(fixedVariables);
  const gradeRates = getGradeDiscountRates(selectedGrades, vars);
  const isInstallationRequired = constructionStatus === '시공';

  const privateInputAmount = getPrivateInputAmount(additionalData);
  const shasiRowsForArea = filterShasiRowsForArea(shasiData);
  const totalArea = calculateTotalArea(shasiData, vars.areaUnit);
  const totalQuantity = shasiRowsForArea.reduce((sum, row) => sum + (parseInt(row.quantity) || 1), 0);
  const splitAmount = calculateSplitAmount(privateInputAmount, totalQuantity);

  return shasiData.map((row) => {
    const w = parseFloat(row.width) || 0;
    const h = parseFloat(row.height) || 0;
    const item = row.item?.toUpperCase();

    // 빈 항목
    if (!item || item === '-') {
      return { ...row, homeWindowPrice: 0, lxHausysPrice: 0, kccHomeCCPrice: 0 };
    }

    // 랩핑추가
    if (item === '랩핑추가') {
      const wrappingPrices = calculateWrappingPrice(shasiData, unitPrices, vars.areaUnit, vars.wrappingPercent);
      return {
        ...row,
        homeWindowPrice: wrappingPrices.homeWindow,
        lxHausysPrice: wrappingPrices['LX하우시스'],
        kccHomeCCPrice: wrappingPrices['KCC홈CC'],
      };
    }

    // 가로/세로가 없는 경우
    if (w === 0 || h === 0) {
      return { ...row, homeWindowPrice: 0, lxHausysPrice: 0, kccHomeCCPrice: 0 };
    }

    const area = calculateArea(w, h, vars.areaUnit);
    const q = parseInt(row.quantity) || 1;

    // 시공비 (망추가 제외) — 단위면적 기준 배분 (반올림 후 수량 곱하기)
    const installationFee =
      item !== '망추가(블랙스텐망)'
        ? calculateInstallationFee(area, totalArea, isInstallationRequired, vars)
        : 0;

    // 운송비 (망추가 제외) — 단위면적 기준 배분
    const transportFee =
      totalArea > 0 && item !== '망추가(블랙스텐망)'
        ? calculateTransportFee(area, totalArea, vars.baseTransportFee)
        : 0;

    // 단가 조회
    let homeWindowPrice = unitPrices['homeWindow']?.[item]?.[area] || 0;
    let lxHausysPrice = unitPrices['LX하우시스']?.[item]?.[area] || 0;
    let kccHomeCCPrice = unitPrices['KCC홈씨씨']?.[item]?.[area] || 0;

    if (item !== '망추가(블랙스텐망)') {
      // 단위당 가격 산출 → 반올림(1000원) → 수량 곱하기
      // 3행×수량1 총합 = 1행×수량3 총합이 보장됨
      // 홈윈도우
      const homeWindowMaterialPrice = homeWindowPrice * (1 + vars.homeWindowMarkupRate) * (1 - gradeRates.homewindow);
      const homeWindowDiscountAmount = homeWindowMaterialPrice * (Number(discountRates.homeWindow) || 0) / 100;
      const hwPerUnit = (homeWindowMaterialPrice - homeWindowDiscountAmount) + installationFee + splitAmount;
      homeWindowPrice = Math.round(hwPerUnit / 1000) * 1000 * q;

      // LX하우시스 + 운송비
      const lxMaterialPrice = lxHausysPrice * (1 + vars.lxHausysMarkupRate) * (1 - gradeRates.lx);
      const lxDiscountAmount = lxMaterialPrice * (Number(discountRates['LX하우시스']) || 0) / 100;
      const lxPerUnit = (lxMaterialPrice - lxDiscountAmount) + installationFee + transportFee + splitAmount;
      lxHausysPrice = Math.round(lxPerUnit / 1000) * 1000 * q;

      // KCC홈CC (인하율 없음) + 운송비
      const kccMaterialPrice = kccHomeCCPrice * (1 + vars.kccHomeCCMarkupRate);
      const kccDiscountAmount = kccMaterialPrice * (Number(discountRates['KCC홈CC']) || 0) / 100;
      const kccPerUnit = (kccMaterialPrice - kccDiscountAmount) + installationFee + transportFee + splitAmount;
      kccHomeCCPrice = Math.round(kccPerUnit / 1000) * 1000 * q;
    } else {
      // 망추가(블랙스텐망)는 단위가격 반올림 후 수량 곱하기
      homeWindowPrice = Math.round(homeWindowPrice / 1000) * 1000 * q;
      lxHausysPrice = Math.round(lxHausysPrice / 1000) * 1000 * q;
      kccHomeCCPrice = Math.round(kccHomeCCPrice / 1000) * 1000 * q;
    }

    return {
      ...row,
      homeWindowPrice: Math.round(homeWindowPrice / 1000) * 1000,
      lxHausysPrice: Math.round(lxHausysPrice / 1000) * 1000,
      kccHomeCCPrice: Math.round(kccHomeCCPrice / 1000) * 1000,
    };
  });
}

// ──────────────────────────────────────────────
// 할인 표시 금액 계산 (discountData)
// ──────────────────────────────────────────────

export function calculateDiscountData(
  shasiData: ShasiRow[],
  params: DiscountCalcParams
): BrandPrices {
  const { unitPrices, fixedVariables, discountRates, selectedGrades } = params;

  const vars = extractFixedVars(fixedVariables);
  const gradeRates = getGradeDiscountRates(selectedGrades, vars);

  let totalHomeWindowDiscount = 0;
  let totalLxDiscount = 0;
  let totalKccDiscount = 0;

  shasiData.forEach((row) => {
    const w = parseFloat(row.width) || 0;
    const h = parseFloat(row.height) || 0;
    const item = row.item?.toUpperCase();

    if (!item || item === '-' || item === '랩핑추가' || item === '망추가(블랙스텐망)' || w === 0 || h === 0) {
      return;
    }

    const area = calculateArea(w, h, vars.areaUnit);

    const homeWindowUnitPrice = unitPrices['homeWindow']?.[item]?.[area] || 0;
    const lxUnitPrice = unitPrices['LX하우시스']?.[item]?.[area] || 0;
    const kccUnitPrice = unitPrices['KCC홈씨씨']?.[item]?.[area] || 0;

    const q = parseInt(row.quantity) || 1;
    const homeWindowMaterialPrice = homeWindowUnitPrice * (1 + vars.homeWindowMarkupRate) * (1 - gradeRates.homewindow);
    const lxMaterialPrice = lxUnitPrice * (1 + vars.lxHausysMarkupRate) * (1 - gradeRates.lx);
    const kccMaterialPrice = kccUnitPrice * (1 + vars.kccHomeCCMarkupRate);

    totalHomeWindowDiscount += homeWindowMaterialPrice * (Number(discountRates.homeWindow) || 0) / 100 * q;
    totalLxDiscount += lxMaterialPrice * (Number(discountRates['LX하우시스']) || 0) / 100 * q;
    totalKccDiscount += kccMaterialPrice * (Number(discountRates['KCC홈CC']) || 0) / 100 * q;
  });

  return {
    homeWindow: -Math.round(totalHomeWindowDiscount * 1.1 / 1000) * 1000,
    'LX하우시스': -Math.round(totalLxDiscount * 1.1 / 1000) * 1000,
    'KCC홈CC': -Math.round(totalKccDiscount * 1.1 / 1000) * 1000,
  };
}

// ──────────────────────────────────────────────
// 최종 합계 + 부가세 (calculateEstimate 내부 로직)
// ──────────────────────────────────────────────

export function calculateTotals(
  shasiData: ShasiRow[],
  otherData: OtherRow[],
  tongbaData: TongbaRow[],
  additionalData: AdditionalRow[]
): Totals {
  // 브랜드별 샷시 금액 합산
  const totalHomeWindow = shasiData.reduce((sum, row) => sum + (row.homeWindowPrice || 0), 0);
  const totalLXHausys = shasiData.reduce((sum, row) => sum + (row.lxHausysPrice || 0), 0);
  const totalKCCHomeCC = shasiData.reduce((sum, row) => sum + (row.kccHomeCCPrice || 0), 0);

  // 기타 금액 합산 (비공개 금액 제외)
  const otherSum = otherData
    .filter((row) => row.item && row.item !== '-')
    .reduce((sum, row) => sum + (row.price || 0), 0);

  const tongbaSum = tongbaData
    .filter((row) => row.item && row.item !== '-')
    .reduce((sum, row) => sum + (row.price || 0), 0);

  const additionalSum = additionalData
    .filter(
      (row) =>
        row.item &&
        row.item !== '-' &&
        !PRIVATE_ITEMS.includes(row.item)
    )
    .reduce((sum, row) => sum + (row.price || 0), 0);

  const excludedFromDiscount = tongbaSum + additionalSum;
  const etcTotal = otherSum + tongbaSum + additionalSum;

  // 부가세 포함 가격 (할인은 이미 적용됨)
  const homeWindowWithVATBase = Math.round(totalHomeWindow * 1.1);
  const lxHausysWithVATBase = Math.round(totalLXHausys * 1.1);
  const kccHomeCCWithVATBase = Math.round(totalKCCHomeCC * 1.1);

  // 최종 금액 = 샷시(할인 적용됨) + 중문&기타 + 통바/추가사항
  const homeWindowAfterDiscount = homeWindowWithVATBase + Math.round(otherSum * 1.1) + Math.round(excludedFromDiscount * 1.1);
  const lxHausysAfterDiscount = lxHausysWithVATBase + Math.round(otherSum * 1.1) + Math.round(excludedFromDiscount * 1.1);
  const kccHomeCCAfterDiscount = kccHomeCCWithVATBase + Math.round(otherSum * 1.1) + Math.round(excludedFromDiscount * 1.1);

  // 공급가 총 합계
  const homeWindowSum = totalHomeWindow + etcTotal;
  const lxHausysSum = totalLXHausys + etcTotal;
  const kccHomeCCSum = totalKCCHomeCC + etcTotal;

  // 부가세 포함 총 가격
  const homeWindowWithVAT = Math.round(homeWindowSum * 1.1);
  const lxHausysWithVAT = Math.round(lxHausysSum * 1.1);
  const kccHomeCCWithVAT = Math.round(kccHomeCCSum * 1.1);

  return {
    homeWindow: { sum: homeWindowSum, vat: homeWindowWithVAT, final: homeWindowAfterDiscount },
    lxHausys: { sum: lxHausysSum, vat: lxHausysWithVAT, final: lxHausysAfterDiscount },
    kccHomeCC: { sum: kccHomeCCSum, vat: kccHomeCCWithVAT, final: kccHomeCCAfterDiscount },
  };
}
